// กล้องของหน้าสแกน — เปิดเองเมื่อเข้าโหมดกล้อง อ่านทุกเฟรมใน Web Worker (omr/scanWorker.js)
// ด้วยตัวอ่านตัวเดียวกับไฟล์ PDF แต่หามุมแบบภาพกล้อง (readCameraFrame) ภาพไม่ถูกส่งขึ้นเซิร์ฟเวอร์
//
//   * ต้องอ่านได้ "แผ่นเดิม คำตอบเดิม" สองเฟรมติดกันจึงส่งให้หน้าหลัก — กันภาพเบลอตอนมือยังขยับ
//   * แผ่นที่เพิ่งส่งไปแล้วยังอยู่หน้ากล้อง → ไม่ส่งซ้ำ จนกว่าจะเห็นแผ่นอื่นหรือไม่เห็นกระดาษ
//   * บันทึกหรือไม่ หน้าหลักเป็นคนตัดสิน (scanSession.decide) ตัวนี้แค่อ่าน
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button } from 'react-bootstrap'

const FRAME_MS = 350        // เว้นระหว่างเฟรม — ตัวอ่านบนมือถือใช้ราว 200-400 มส. ถี่กว่านี้ก็แค่รอคิว
const MAX_SIDE = 1600       // ย่อเฟรมก่อนอ่าน — ละเอียดกว่านี้ไม่ช่วยอ่านช่อง 4.8 มม. แต่ช้าลงมาก
const STABLE_FRAMES = 2
const SOUND_KEY = 'examScanSound'

const loadSound = () => {
  try { return localStorage.getItem(SOUND_KEY) !== '0' } catch { return true }
}

// เสียงสูงสั้น = บันทึกแล้ว / เสียงต่ำยาว = รอครูดู
export const tone = (ok, sound = loadSound()) => {
  if (sound) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      const ctx = new Ctx()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = ok ? 1046 : 330
      gain.gain.value = 0.15
      osc.connect(gain).connect(ctx.destination)
      osc.start()
      osc.stop(ctx.currentTime + (ok ? 0.12 : 0.3))
      osc.onended = () => ctx.close()
    } catch { /* ไม่มีเสียงก็ไม่เป็นไร */ }
  }
  try { navigator.vibrate?.(ok ? 60 : [80, 60, 80]) } catch { /* ไม่รองรับ */ }
}

export const cameraAvailable = () => !!(window.isSecureContext && navigator.mediaDevices?.getUserMedia)

const HINT_COLORS = { idle: 'rgba(0,0,0,.6)', hold: 'rgba(13,110,253,.85)', done: 'rgba(25,135,84,.9)', warn: 'rgba(255,193,7,.9)', bad: 'rgba(220,53,69,.9)' }
const QUAD_COLORS = { idle: '#ffc107', hold: '#0d6efd', done: '#20c997', warn: '#ffc107', bad: '#dc3545' }

export default function ScanCamera({ exam, questions, paused, resetToken, onRead }) {
  const [state, setState] = useState('starting')     // starting | live | error
  const [error, setError] = useState(null)
  const [hint, setHint] = useState({ kind: 'idle', text: 'เล็งให้เห็นสี่เหลี่ยมดำครบ 4 มุม' })
  const [quad, setQuad] = useState(null)
  const [frameSize, setFrameSize] = useState(null)
  const [flash, setFlash] = useState(false)
  const [torch, setTorch] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)
  const [sound, setSound] = useState(loadSound)

  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const workerRef = useRef(null)
  const canvasRef = useRef(null)
  const busyRef = useRef(false)
  const timerRef = useRef(null)
  const stableRef = useRef({ key: null, count: 0 })
  const doneRef = useRef(null)       // แผ่นที่เพิ่งส่งไป — ไม่ส่งซ้ำขณะยังอยู่หน้ากล้อง
  const pausedRef = useRef(paused)
  const wakeRef = useRef(null)
  const genRef = useRef(0)
  const propsRef = useRef({ exam, questions, onRead })
  useEffect(() => { propsRef.current = { exam, questions, onRead } })
  useEffect(() => { pausedRef.current = paused }, [paused])
  useEffect(() => { doneRef.current = null }, [resetToken])

  const onResult = useCallback((res, canvas) => {
    if (pausedRef.current) return
    setQuad(res.markers || null)
    if (!res.found) {
      stableRef.current = { key: null, count: 0 }
      doneRef.current = null
      setHint({ kind: 'idle', text: 'เล็งให้เห็นสี่เหลี่ยมดำครบ 4 มุม' })
      return
    }
    if (res.wrongExam) {
      setHint({ kind: 'bad', text: 'กระดาษของข้อสอบชุดอื่น' })
      return
    }
    if (!res.ok) {
      setHint({ kind: 'warn', text: res.error || 'ถือนิ่งๆ ให้แถบรหัสชัด' })
      return
    }
    const sheetKey = res.spare ? `spare:${res.idRead}` : `s:${res.studyId}`
    if (doneRef.current === sheetKey) {
      setHint({ kind: 'done', text: 'แผ่นนี้อ่านแล้ว — วางแผ่นถัดไปได้เลย' })
      return
    }
    const key = `${sheetKey}|${(res.rows || []).map((r) => r.answer).join(',')}`
    stableRef.current = stableRef.current.key === key
      ? { key, count: stableRef.current.count + 1 }
      : { key, count: 1 }
    if (stableRef.current.count < STABLE_FRAMES) {
      setHint({ kind: 'hold', text: 'ถือนิ่งๆ…' })
      return
    }
    stableRef.current = { key: null, count: 0 }
    doneRef.current = sheetKey
    setFlash(true)
    setTimeout(() => setFlash(false), 400)
    setHint({ kind: 'done', text: 'อ่านแล้ว — วางแผ่นถัดไปได้เลย' })
    propsRef.current.onRead(res, canvas)
  }, [])

  const tick = useCallback(() => {
    timerRef.current = setTimeout(tick, FRAME_MS)
    const video = videoRef.current
    const { exam: ex, questions: qs } = propsRef.current
    if (busyRef.current || pausedRef.current || !video || !workerRef.current || video.readyState < 2 || document.hidden) return
    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!vw || !vh) return
    const scale = Math.min(1, MAX_SIDE / Math.max(vw, vh))
    const w = Math.round(vw * scale)
    const h = Math.round(vh * scale)
    // เฟรมละ canvas ใหม่ — หน้าหลักใช้ canvas ของเฟรมที่อ่านได้ตัดภาพรายข้อ ถ้าใช้ตัวเดียวซ้ำจะโดนเฟรมถัดไปวาดทับ
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    ctx.drawImage(video, 0, 0, w, h)
    canvasRef.current = canvas
    const rgba = ctx.getImageData(0, 0, w, h).data.buffer
    busyRef.current = true
    setFrameSize((f) => (f && f.w === w && f.h === h ? f : { w, h }))
    const id = Date.now()
    const worker = workerRef.current
    const handle = (e) => {
      if (e.data.id !== id) return
      worker.removeEventListener('message', handle)
      busyRef.current = false
      onResult(e.data.res, canvas)
    }
    worker.addEventListener('message', handle)
    worker.postMessage({ id, width: w, height: h, rgba, exam: ex, questions: qs }, [rgba])
  }, [onResult])

  const stop = useCallback(() => {
    genRef.current++
    clearTimeout(timerRef.current)
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    workerRef.current?.terminate()
    workerRef.current = null
    busyRef.current = false
    wakeRef.current?.release?.().catch(() => {})
    wakeRef.current = null
    setQuad(null)
    setTorch(false)
  }, [])

  const start = useCallback(async () => {
    const gen = ++genRef.current
    setError(null)
    setState('starting')
    if (!cameraAvailable()) {
      setError('เบราว์เซอร์นี้เปิดกล้องไม่ได้ — ต้องเปิดผ่าน https และใช้ Chrome/Safari รุ่นใหม่')
      setState('error')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      })
      // ถูกปิดไประหว่างรออนุญาต (ออกจากหน้า / สลับโหมด / StrictMode เรียกซ้ำ) — อย่าปล่อยกล้องค้าง
      if (gen !== genRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      streamRef.current = stream
      const video = videoRef.current
      video.srcObject = stream
      await video.play()
      if (gen !== genRef.current) return
      const track = stream.getVideoTracks()[0]
      const caps = track.getCapabilities?.() || {}
      setTorchSupported(!!caps.torch)
      if (caps.focusMode?.includes('continuous')) {
        track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {})
      }
      workerRef.current = new Worker(new URL('./omr/scanWorker.js', import.meta.url))
      try { wakeRef.current = await navigator.wakeLock?.request('screen') } catch { /* จอดับเองได้ ไม่เป็นไร */ }
      setState('live')
      tick()
    } catch (err) {
      if (gen !== genRef.current) return
      stop()
      setError(err.name === 'NotAllowedError'
        ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง — เปิดสิทธิ์กล้องให้เว็บนี้ในการตั้งค่าเบราว์เซอร์'
        : `เปิดกล้องไม่ได้: ${err.message}`)
      setState('error')
    }
  }, [tick, stop])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track) return
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch }] })
      setTorch(!torch)
    } catch { setTorchSupported(false) }
  }

  const toggleSound = () => {
    const next = !sound
    setSound(next)
    try { localStorage.setItem(SOUND_KEY, next ? '1' : '0') } catch { /* ไม่เป็นไร */ }
    if (next) tone(true, true)
  }

  if (state === 'error') {
    return (
      <Alert variant="danger" className="fs-sm mx-auto" style={{ maxWidth: 520 }}>
        <div className="mb-2">{error}</div>
        <div className="d-flex gap-2 align-items-center">
          <Button size="sm" variant="outline-danger" onClick={start}><i className="ri-refresh-line"></i> ลองอีกครั้ง</Button>
          <span className="text-secondary">หรือเปลี่ยนไปใช้ "ไฟล์ PDF / รูป" ด้านบน</span>
        </div>
      </Alert>
    )
  }

  const roundBtn = { width: 40, height: 40, background: 'rgba(0,0,0,.5)', border: 0 }

  return (
    <div className="position-relative bg-dark rounded overflow-hidden mx-auto" style={{ maxWidth: 520 }}>
      <video ref={videoRef} playsInline muted className="w-100 d-block" style={{ minHeight: 240 }} />
      {quad && frameSize && (
        <svg className="position-absolute top-0 start-0 w-100 h-100" viewBox={`0 0 ${frameSize.w} ${frameSize.h}`} preserveAspectRatio="none">
          <polygon points={quad.map((m) => `${m.x},${m.y}`).join(' ')} fill="none"
            stroke={QUAD_COLORS[hint.kind]} strokeWidth={Math.max(3, frameSize.w / 200)} />
        </svg>
      )}
      {flash && <div className="position-absolute top-0 start-0 w-100 h-100" style={{ boxShadow: 'inset 0 0 0 8px #20c997', background: 'rgba(32,201,151,.15)' }} />}
      <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">
        {torchSupported && (
          <button type="button" className="rounded-circle text-white" style={{ ...roundBtn, background: torch ? '#ffc107' : roundBtn.background }}
            aria-label={torch ? 'ปิดไฟแฟลช' : 'เปิดไฟแฟลช'} aria-pressed={torch} onClick={toggleTorch}>
            <i className="ri-flashlight-line"></i>
          </button>
        )}
        <button type="button" className="rounded-circle text-white" style={roundBtn}
          aria-label={sound ? 'ปิดเสียงตอนสแกน' : 'เปิดเสียงตอนสแกน'} aria-pressed={sound} onClick={toggleSound}>
          <i className={sound ? 'ri-volume-up-line' : 'ri-volume-mute-line'}></i>
        </button>
      </div>
      <div className="position-absolute bottom-0 start-0 w-100 text-center text-white fw-semibold py-2 px-3" role="status"
        style={{ background: HINT_COLORS[hint.kind] }}>
        {state === 'starting' ? 'กำลังเปิดกล้อง…' : paused ? 'หยุดชั่วคราว' : hint.text}
      </div>
    </div>
  )
}
